/**
 * Cliente de autenticação do Supabase.
 *
 * **Só autenticação — e a dependência reflete isso.** Em vez de
 * `@supabase/supabase-js`, que traz PostgREST, Storage, Realtime e Functions,
 * aqui está apenas `@supabase/auth-js`. São 30 kB comprimidos a menos no
 * primeiro carregamento, num app mobile-first usado no wi-fi de academia.
 *
 * O ganho maior não é o tamanho: é que a decisão de arquitetura passa a estar
 * no `package.json`. **O frontend não lê nem escreve tabela pelo Supabase** —
 * todo dado passa pela API, que aplica as regras de domínio e injeta o contexto
 * de RLS por transação. Com o cliente completo, contornar isso seria uma linha
 * de código e nenhuma revisão pegaria. Sem ele, não há como: o método não
 * existe.
 *
 * (E não funcionaria mesmo: as tabelas vivem no schema `movhuman`, e a chave
 * anônima não tem acesso a ele. O que se ganharia era um erro obscuro em vez de
 * um erro de compilação.)
 */

import { AuthClient, type Session } from '@supabase/auth-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Falha no import, não na primeira tentativa de login: um build sem essas
  // variáveis não serve para nada, e é melhor descobrir na tela em branco do
  // que no formulário.
  throw new Error(
    'VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY são obrigatórias. Veja .env.example.',
  );
}

export const auth = new AuthClient({
  url: `${url.replace(/\/$/, '')}/auth/v1`,
  // O Kong do Supabase exige `apikey` em toda requisição — é o key-auth que faz
  // `/auth/v1/health` responder 401 sem ele, como visto na Fase 4.
  headers: { apikey: anonKey, Authorization: `Bearer ${anonKey}` },
  storageKey: 'movhuman.auth',
  // PKCE: o código de autorização só vale acompanhado do verificador que ficou
  // no dispositivo. Sem ele, um link de confirmação interceptado bastaria para
  // assumir a sessão.
  flowType: 'pkce',
  persistSession: true,
  autoRefreshToken: true,
  detectSessionInUrl: true,
});

export async function currentAccessToken(): Promise<string | null> {
  const { data } = await auth.getSession();
  return data.session?.access_token ?? null;
}

export type { Session };
